import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DotIcon } from '../../../assets/icons/DotIcon';
import { Collector, CollectorStatus } from '../../models/Collector';
import { Router } from '../../router/Router';
import { COLORS } from '../../utils/Colors';
import { CollectorStatusChangeModal } from '../modals/CollectorStatusChangeModal';
import { Separator } from '../Separator';
import { FillLevelWidget } from './FillLevelWidget';

const Root = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    
    @media screen and (max-width: 768px) {
        padding: 12px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }
`;

const CameraButton = styled.button`
    font-size: 16px;
    background: ${COLORS.BLUE};
    color: ${COLORS.WHITE};
    border: none;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
`;

const TD = styled.td`
    width: 200px;
    font-weight: 500;
    
    @media screen and (max-width: 768px) {
        width: 150px;
        font-size: 14px;
    }
    
    @media screen and (max-width: 480px) {
        width: 120px;
        font-size: 12px;
    }
`;

const ChangeStatusButton = styled.button`
    font-size: 12px;
    color: ${COLORS.WHITE};
    border: none;
    padding: 4px 8px;
    border-radius: 20px;
`;

interface Props {
  collector: Collector;
  setCollector: (collector: Collector) => void;
}

export const CollectorDataWidget: FC<Props> = ({ collector, setCollector }) => {
  const navigation = useNavigate();

  const [isCollectorStatusChangeModalVisible, setIsCollectorStatusChangeModalVisible] = useState<boolean>(false);
  const [statusToChangeTo, setStatusToChangeTo] = useState<CollectorStatus>();

  const handleOpenCollectorStatusChangeModal = (status: CollectorStatus) => {
    setStatusToChangeTo(status);
    setIsCollectorStatusChangeModalVisible(true);
  };

  const handleCameraPreview = () => Router(navigation).goToCameraPreview(collector.id);

  return (
    <>
      <Root>
        <h2>{collector.name}</h2>
        <span>{collector.hw_serial_num}</span>
        <CameraButton type="button" onClick={handleCameraPreview}>
          <i className="fa-solid fa-video" />
        </CameraButton>
        <Separator size="XS" />
        <span style={{ fontWeight: 500 }}>{collector.address}</span>
        <Separator size="S" />
        <table>
          <tbody>
            <tr>
              <TD>Collector ID:</TD>
              <td>{collector.id}</td>
            </tr>
            <tr>
              <TD>Status:</TD>
              <td>
                {(collector.status === 'Active' || collector.status === 'NeedsMaintenance') &&
                  <DotIcon width={16} height={16} color={collector.status === 'Active' ? COLORS.GREEN_2 : COLORS.ORANGE} />}{' '}
                {collector.status}
                {
                  collector.status === 'Active' && (
                    <>
                      {' '}
                      <ChangeStatusButton type="button"
                        style={{ background: COLORS.ORANGE }}
                        onClick={() => handleOpenCollectorStatusChangeModal('NeedsMaintenance')}>
                        Change to NeedsMaintenance
                      </ChangeStatusButton>
                    </>
                  )
                }
                {
                  collector.status === 'NeedsMaintenance' && (
                    <>
                      {' '}
                      <ChangeStatusButton type="button"
                        style={{ background: COLORS.GREEN_2 }}
                        onClick={() => handleOpenCollectorStatusChangeModal('Active')}>
                        Change to Active
                      </ChangeStatusButton>
                    </>
                  )
                }
              </td>
            </tr>
            <tr>
              <TD>Operational status:</TD>
              <td>{collector.operational_status?.toUpperCase()}</td>
            </tr>
            <tr>
              <TD>Running SW version:</TD>
              <td>{collector.current_swv ? `v${collector.current_swv}` : ''}</td>
            </tr>
            <tr>
              <TD>Target SW version:</TD>
              <td>{collector.target_swv ? `v${collector.target_swv}` : ''}</td>
            </tr>
          </tbody>
        </table>
        <Separator size="XS" />
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 10,
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}>
          <FillLevelWidget collector={collector} type="ALU" />
          <FillLevelWidget collector={collector} type="GLS" />
          <FillLevelWidget collector={collector} type="PET" />
        </div>
      </Root>
      {isCollectorStatusChangeModalVisible && <CollectorStatusChangeModal
        onCancel={() => setIsCollectorStatusChangeModalVisible(false)}
        collector={collector}
        statusToChangeTo={statusToChangeTo}
        setCollector={setCollector}
      />}
    </>
  );
};
